import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "@/services/auth.service";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-600 text-background-50">
            <i className="ri-home-4-line text-2xl"></i>
          </span>
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-4">
            Nomad Livings <span className="italic text-primary-700">Admin</span>
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            Sign in to manage your properties, leads and reports.
          </p>
          <div className="mt-4 rounded-lg bg-secondary-100 border border-secondary-200 px-4 py-3 text-xs text-secondary-900 flex items-start gap-2">
            <i className="ri-timer-line mt-0.5"></i>
            <span>
              All sessions are automatically logged out daily at 00:00. You&apos;ll simply need
              to sign in again.
            </span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-3xl bg-background-50 border border-background-200 p-6 md:p-8 space-y-5"
        >
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-foreground-800 mb-1.5">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@nomadlivings.com"
              className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-semibold text-foreground-800 mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 pr-11 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 text-foreground-500 hover:text-foreground-800"
                aria-label="Toggle password visibility"
              >
                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
              <i className="ri-error-warning-line mr-1.5"></i>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3.5 hover:bg-primary-700 transition-colors disabled:opacity-60"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i> Signing in...
              </>
            ) : (
              <>
                Sign In <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>

          <p className="text-center text-[11px] text-foreground-600">
            <a href="/" className="hover:text-primary-700">
              <i className="ri-arrow-left-line mr-1"></i>Back to website
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}